import { useLocation, useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Grid from "../../components/grid/Grid";
import {
  getListDirectory,
  postRequestForLatestDirectory,
  downloadListDirectory,
  uploadSelectedFiles,
} from "../../services/FileFromMachineAPI";
import { useState, useEffect, useRef } from "react";
import dayjs from "dayjs";
import "./FileBrowsing.css";
import TreeView from "../../components/Tree Structure/TreeView";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { faDownload, faSpinner, faRefresh } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatToBrowserTime } from "../../utils/timeFormat";
import RequestUpdateButton from "../../components/directoryRequest/RequestUpdateButton";

const FileBrowsing = () => {
  const { t } = useTranslation();
  const ctx = useOutletContext?.() || {};
  const location = useLocation();
  const qs = new URLSearchParams(location?.search ?? "");
  const model = ctx?.model ?? qs.get("model") ?? "";
  const serialNumber = ctx?.serialNumber ?? qs.get("serialNumber") ?? "";

  const [treeItems, setTreeItems] = useState([]);
  const [lastUpdatedDate, setLastUpdatedDate] = useState("");
  const [selectedNodePath, setSelectedNodePath] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [data, setData] = useState([]);
  const [treeLoading, setTreeLoading] = useState(false);
  const [fileLoading, setFileLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);

  const hasCheckedInitialDirectory = useRef(false);

  const headers = [
    { key: "name", label: "File Name", width: "35%" },
    { key: "size", label: "Size", width: "30%" },
    { key: "date", label: "Date", width: "35%" },
  ];

  const isRequestUpdateInProgress = () => {
    const until = Number(
      localStorage.getItem(`FileFromMachine_requestUpdate_${model}_${serialNumber}`)
    );
    return Boolean(until && until > Date.now());
  };

  const handleTreeClick = async (node) => {
    if (!node?.path) return;

    setSelectedNodePath(node.path);

    try {
      const params = {
        model: model,
        serial: serialNumber,
        directory: node.path,
      };
      setFileLoading(true);

      const table_data = await getListDirectory(params);
      const files = table_data?.directory_listing?.[0]?.File ?? [];

      const mapped = files.map((file, index) => ({
        id: index + 1,
        name: file.name,
        size: file.size,
        date: file?.lastModified ? formatToBrowserTime(file.lastModified) : "",
        path: `${node.path}/${file.name}`,
      }));

      setData(mapped);
      setFileLoading(false);
    } catch (err) {
      console.error("Error loading directory contents:", err);
      toast.error("Failed to load directory contents");
      setFileLoading(false);
    }
  };

  const downloadFiles = async () => {
    setDownloadLoading(true);
    try {
      const params = {
        model: model,
        serial: serialNumber,
        file_name: "DirectoryListing.json",
      };

      const res = await downloadListDirectory(params);
      const url = res?.downloadUrl;
      if (!url) throw new Error("No presigned URL returned from server.");
      const filename = res?.zip_key
        ? `${res.zip_key}.zip`
        : decodeURIComponent(new URL(url).pathname.split("/").pop() || "download");
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.rel = "noreferrer";
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("Download started");
    } catch (err) {
      console.error("Download error:", err);
      toast.error("Failed to start download");
    } finally {
      setDownloadLoading(false);
    }
  };

  const loadData = async () => {
    try {
      setTreeLoading(true);

      if (!model || !serialNumber) {
        return;
      }

      const params = { model: model, serial: serialNumber };
      const raw = await getListDirectory(params);

      if (!raw || raw.length < 1) {
        const requestInProgress = isRequestUpdateInProgress();

        if (
          !hasCheckedInitialDirectory.current &&
          !requestInProgress
        ) {
          toast.error(
            "No directory listing found. Please click 'Request Update' to fetch the latest data."
          );
        }

        hasCheckedInitialDirectory.current = true;
        return;
      }

      const lastModified = raw?.last_updated ? dayjs(raw.last_updated).format("YYYY-MM-DD HH:mm") : "";
      setLastUpdatedDate(lastModified);

      if (raw?.directory_listing?.length > 0) {
        hasCheckedInitialDirectory.current = true;
        const treeViewFormatted = raw.directory_listing.map((item) => ({
          name: item.displayName,
          path: item.path,
          children: [],
        }));
        setTreeItems(treeViewFormatted);

        // Auto-select first item
        const firstItem = treeViewFormatted[0];
        if (firstItem) {
          handleTreeClick(firstItem);
        }
      }
    } catch (err) {
      console.error("Load data error:", err);
      toast.error("Failed to load directory listing");
    } finally {
      setTreeLoading(false);
    }
  };

  const handleUploadFiles = async () => {
    if (selectedRows.length === 0) {
      toast.error("No files selected for upload.");
      return;
    }

    const filePaths = selectedRows.map((row) => row.path || row.name);

    const body = {
      model: model,
      serial: serialNumber,
      file_paths: filePaths,
    };

    try {
      const response = await uploadSelectedFiles(body);
      if (response) {
        toast.success(`${selectedRows.length} File(s) requested for Upload.`)
      }
    } catch (err) {
      console.error("Upload failed:", err);
      toast.error("Failed to upload file");
    }
  };

  // initial load
  useEffect(() => {
    hasCheckedInitialDirectory.current = false;
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, serialNumber]);

  return (
    <div className="fb-page-wrapper">
      <div className="fb-header">
        <div className="fs-header-update">
          <span className="fs-label">Last Updated- {lastUpdatedDate} </span>
          <RequestUpdateButton
            scope={"FileFromMachine"}
            model={model}
            serial={serialNumber}
            requestFn={() =>
              postRequestForLatestDirectory({ model, serial: serialNumber })
            }
            onSuccess={loadData}
          />
        </div>
        <div className="fs-header-buttons">
          <button className="btn btn-download" onClick={downloadFiles} disabled={treeItems.length === 0 || downloadLoading}>
            <FontAwesomeIcon icon={faDownload} className="me-1" />
            {t(`Download file Listing`) || `Download file Listing`}
            {downloadLoading && <FontAwesomeIcon icon={faSpinner} spin className="ms-2" />}
          </button>

          <button className="btn btn-primary" onClick={() => loadData()}>
            <FontAwesomeIcon icon={faRefresh} className="me-1" />
            Refresh
          </button>
        </div>
      </div>

      <div className="fb-header-content">
        <aside className="fb-sidebar">
          <div className="fb-path-label">Path:- {selectedNodePath}</div>
          <div className="fb-treeview-container">
            <TreeView data={treeItems} onNodeClick={handleTreeClick} selectedNodePath={selectedNodePath} loading={treeLoading} isFlat={true} />
          </div>
        </aside>
        <main className="fb-main-content">
          <div className="fb-actions">
            <button className="btn btn-primary" onClick={handleUploadFiles} disabled={selectedRows.length === 0}>
              {t('Upload Selected')}
            </button>
          </div>

          <div className="fb-content">
            <div className="grid-wrapper">
              <Grid
                loading={fileLoading}
                headers={headers}
                data={data}
                rowsPerPage={50}
                canMultiSelect={true}
                showPagination={true}
                onSelectionChange={setSelectedRows}
              />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default FileBrowsing;
